function getReadableBitrate(fileSizeInBytes) {
    let i = -1;
    const byteUnits = [
        ' kbps',
        ' Mbps',
        ' Gbps',
        ' Tbps',
        ' Pbps',
        ' Ebps',
        ' Zbps',
        ' Ybps',
    ];
    let size = fileSizeInBytes;
    do {
        size /= 1024;
        i++;
    } while (size > 1024);
    return Math.max(size, 0.1).toFixed(1) + byteUnits[i];
}
export function estimateScore(score) {
    switch (true) {
        case score > 4:
            return 'excellent';
        case score > 3:
            return 'fair';
        default:
            return 'poor';
    }
}
export function estimate(data) {
    if (!navigator.onLine) {
        data.estimation = 'disconnected';
        return data;
    }
    if (data.packetsLost > 0.075) {
        // <- fine tune this number
        data.estimation = 'unstable';
        return data;
    }
    data.estimation = estimateScore(data.score);
    return data;
}
function targetBitrateForPixelCount(pixelCount) {
    const y = 2.069924867 * Math.pow(Math.log10(pixelCount), 0.6250223771);
    return Math.pow(10, y);
}
function safeNumber(input) {
    const num = typeof input !== 'number' ? 0 : input;
    return Number.isNaN(num) ? 0 : num;
}
export function calculateVideoScore(videoEl, stats, isPublisher = false) {
    const LD_PIXEL_COUNT = 320 * 240;
    const LD_BITRATE_CALC_OFFSET = 1.5;
    const packetsAttrName = isPublisher ? 'packetsSent' : 'packetsReceived';
    const bytesAttrName = isPublisher ? 'bytesSent' : 'bytesReceived';
    const MIN_VIDEO_BITRATE = 30000;
    if (stats.length < 2) {
        return { score: null };
    }
    const currentStats = stats[stats.length - 1];
    const lastStats = stats[stats.length - 2];
    if (!currentStats.video || !lastStats.video) {
        return { score: null };
    }
    const currentPacketsAttrValue = safeNumber(currentStats.video[packetsAttrName]);
    const lastPacketsAttrValue = safeNumber(lastStats.video[packetsAttrName]);
    const currentBytesAttrValue = safeNumber(currentStats.video[bytesAttrName]);
    const lastBytesAttrValue = safeNumber(lastStats.video[bytesAttrName]);
    const currentPacketsLost = safeNumber(currentStats.video.packetsLost);
    const lastPacketsLost = safeNumber(lastStats.video.packetsLost);
    const totalPackets = currentPacketsLost +
        currentPacketsAttrValue -
        (lastPacketsLost + lastPacketsAttrValue);
    const packetsLost = (currentPacketsLost - lastPacketsLost) / totalPackets;
    const interval = currentStats.timestamp - lastStats.timestamp;
    let bitrate = (8 * (currentBytesAttrValue - lastBytesAttrValue)) / (interval / 1000);
    const resolution = videoEl
        ? `${videoEl.videoWidth} x ${videoEl.videoHeight}`
        : `320 x 240`;
    const pixelCount = videoEl
        ? videoEl.videoWidth * videoEl.videoHeight
        : LD_PIXEL_COUNT;
    const targetBitrate = targetBitrateForPixelCount(pixelCount);
    const frameRate = safeNumber(currentStats.video.frameRate);
    if (bitrate < MIN_VIDEO_BITRATE) {
        return { score: 0 };
    }
    bitrate = Math.min(bitrate, targetBitrate);
    const isLDVideo = pixelCount <= LD_PIXEL_COUNT;
    // Increasing actual bitrate only for MOS calculations cuz of weird numbers on low resolution
    const bitrateForCalculations = isLDVideo
        ? bitrate * LD_BITRATE_CALC_OFFSET
        : bitrate;
    const score = (Math.log(bitrateForCalculations / MIN_VIDEO_BITRATE) /
        Math.log(targetBitrate / MIN_VIDEO_BITRATE)) *
        4 +
        1;
    const data = {
        score,
        packetsLost,
        bitrate: getReadableBitrate(bitrate),
        frameRate,
        resolution,
    };
    return estimate(data);
}
function H(x) {
    return x < 0 ? 0 : 1;
}
//R = 94.2 − Id − Ie
function R(params) {
    const { a, b, c, rtt, packetLoss, LOCAL_DELAY } = params;
    const d = rtt + LOCAL_DELAY;
    const Id = 0.024 * d + 0.11 * (d - 177.3) * H(d - 177.3);
    const P = packetLoss;
    const Ie = a + b * Math.log(1 + c * P);
    const output = 94.2 - Id - Ie;
    return output;
}
function MOS(r) {
    if (r < 0) {
        return 1;
    }
    if (r > 100) {
        return 4.5;
    }
    return 1 + 0.035 * r + (7.1 / 1000000) * r * (r - 60) * (100 - r);
}
function audioScore(rtt, packetLoss) {
    const LOCAL_DELAY = 20; //20 msecs: typical frame duration
    const a = 0; // ILBC: a=10
    const b = 19.8;
    const c = 29.7;
    //For R < 0: MOS = 1
    //For 0 R 100: MOS = 1 + 0.035 R + 7.10E-6 R(R-60)(100-R)
    //For R > 100: MOS = 4.5
    return MOS(R({ LOCAL_DELAY, a, b, c, rtt, packetLoss }));
}
export function calculateAudioScore(stats, isPublisher = false) {
    const packetsAttrName = isPublisher ? 'packetsSent' : 'packetsReceived';
    const bytesAttrName = isPublisher ? 'bytesSent' : 'bytesReceived';
    if (stats.length < 2) {
        return { score: null };
    }
    const currentStats = stats[stats.length - 1];
    const lastStats = stats[stats.length - 2];
    const interval = currentStats.timestamp - lastStats.timestamp;
    if (!currentStats.audio || !lastStats.audio) {
        return { score: null };
    }
    const currentPacketsAttrValue = safeNumber(currentStats.audio[packetsAttrName]);
    const lastPacketsAttrValue = safeNumber(lastStats.audio[packetsAttrName]);
    const currentBytesAttrValue = safeNumber(currentStats.audio[bytesAttrName]);
    const lastBytesAttrValue = safeNumber(lastStats.audio[bytesAttrName]);
    const currentPacketsLost = safeNumber(currentStats.audio.packetsLost);
    const lastPacketsLost = safeNumber(lastStats.audio.packetsLost);
    const totalAudioPackets = currentPacketsLost -
        lastPacketsLost +
        (currentPacketsAttrValue - lastPacketsAttrValue);
    if (0 === totalAudioPackets) {
        return { score: 0 };
    }
    const packetLoss = (currentPacketsLost - lastPacketsLost) / totalAudioPackets;
    // missing from js getStats :-(
    const rtt = 0;
    const score = audioScore(rtt, packetLoss);
    const bitrate = (8 * (currentBytesAttrValue - lastBytesAttrValue)) / (interval / 1000);
    const data = {
        score,
        timestamp: currentStats.timestamp,
        packetsLost: packetLoss,
        bitrate: getReadableBitrate(bitrate),
    };
    return estimate(data);
}
export function isPoorVideo(videoScoresLog, params) {
    const { consecutiveItemsCount, scoreLessThan, frameRateLessThan } = params;
    if (videoScoresLog.length < consecutiveItemsCount) {
        return false;
    }
    return videoScoresLog
        .slice(consecutiveItemsCount < videoScoresLog.length
        ? videoScoresLog.length - consecutiveItemsCount
        : 0)
        .every(function (scoreLogItem) {
        return (safeNumber(scoreLogItem.score) < scoreLessThan ||
            safeNumber(scoreLogItem.frameRate) < frameRateLessThan);
    });
}
export function isGoodAudio(audioScoresLog, params) {
    const { consecutiveItemsCount, scoreMoreThan, afterTimestamp } = params;
    if (audioScoresLog.length < consecutiveItemsCount) {
        return false;
    }
    return audioScoresLog
        .slice(consecutiveItemsCount < audioScoresLog.length
        ? audioScoresLog.length - consecutiveItemsCount
        : 0)
        .every(function (scoreLogItem) {
        return (safeNumber(scoreLogItem.score) > scoreMoreThan &&
            scoreLogItem.packetsLost === 0 &&
            safeNumber(scoreLogItem.timestamp) > afterTimestamp);
    });
}
